import { sortArrayByKey } from "@/lib/utils/sorting";
import { CropStage } from "@/types/cropConfiguration";
import { ApplicationPlan, PlantingPlan } from "@/types/cropPlan";
import { Checkbox } from "@heroui/react";
import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import allNHorizontalImage from "@/assets/images/all_n_horizontal.png";

export const plantingPlanColumns = {
    selection: <T extends PlantingPlan>(): ColumnDef<T> => ({
        id: "selection",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
            <Checkbox
                isSelected={row.getIsSelected()}
                onValueChange={(value: boolean) => row.toggleSelected(value)}
            />
        ),
        size: 50,
    }),
    name: <T extends PlantingPlan>(): ColumnDef<T> => ({
        id: "name",
        header: "Plan",
        accessorKey: "name",
        cell: ({ getValue, row }) => {
            if (row.original.key?.startsWith("sentinel_")) {
                return (
                    <div
                        className="font-medium text-default-900 cursor-pointer flex flex-col gap-0 items-start"
                        onClick={() => {
                            row.toggleSelected(!row.getIsSelected());
                        }}
                    >
                        {String(getValue() ?? "—")}
                        <img
                            src={allNHorizontalImage}
                            alt="ALL-N Horizontal"
                            className="h-5"
                        />
                    </div>
                );
            }
            return (
                <span
                    className="font-medium text-default-900 cursor-pointer"
                    onClick={() => {
                        row.toggleSelected(!row.getIsSelected());
                    }}
                >
                    {String(getValue() ?? "—")}
                </span>
            );
        },
        size: 200,
    }),
    organization: <T extends PlantingPlan>(
        organizations: { id: number; description: string }[],
    ): ColumnDef<T> => ({
        id: "organization",
        header: "Organization",
        accessorFn: (row) => row.organization,
        cell: ({ row }) => (
            <span className="text-default-600">
                {
                    organizations.find(
                        (o) => o.id === row.original.organization,
                    )?.description
                }
            </span>
        ),
        size: 200,
    }),
    crop_configuration: <T extends PlantingPlan>(
        cropOptions: { id: number; description: string }[],
    ): ColumnDef<T> => ({
        id: "crop_configuration",
        header: "Crop",
        accessorFn: (row) => row.crop_configuration,
        cell: ({ row }) => (
            <span className="text-default-600">
                {
                    cropOptions.find(
                        (c) => c.id === row.original.crop_configuration,
                    )?.description
                }
            </span>
        ),
        size: 150,
    }),
    date: <T extends PlantingPlan>(): ColumnDef<T> => ({
        id: "date",
        header: "Planting Date",
        accessorFn: (row) => row.date,
        cell: ({ row }) => (
            <span className="text-default-600">
                {dayjs(row.original.date).format("MM/DD/YYYY")}
            </span>
        ),
        size: 150,
    }),
    seeding_rate: <T extends PlantingPlan>(): ColumnDef<T> => ({
        id: "seeding_rate",
        header: "Seeding Rate",
        accessorFn: (row) => row.seeding_rate,
        cell: ({ row }) => (
            <span className="text-default-600">
                {row.original.seeding_rate}
            </span>
        ),
        size: 150,
    }),
    seeding_depth: <T extends PlantingPlan>(): ColumnDef<T> => ({
        id: "seeding_depth",
        header: "Depth",
        accessorFn: (row) => row.seeding_depth,
        cell: ({ row }) => (
            <span className="text-default-600">
                {row.original.seeding_depth}
            </span>
        ),
        size: 80,
    }),
};

export const applicationPlanColumns = {
    selection: <T extends ApplicationPlan>(): ColumnDef<T> => ({
        id: "selection",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
            <Checkbox
                isSelected={row.getIsSelected()}
                onValueChange={(value: boolean) => row.toggleSelected(value)}
            />
        ),
        size: 50,
    }),
    name: <T extends ApplicationPlan>(): ColumnDef<T> => ({
        id: "name",
        header: "Plan",
        accessorKey: "name",
        cell: ({ getValue, row }) => {
            if (row.original.key?.startsWith("sentinel_")) {
                return (
                    <div
                        className="font-medium text-default-900 cursor-pointer flex flex-col gap-0 items-start"
                        onClick={() => {
                            row.toggleSelected(!row.getIsSelected());
                        }}
                    >
                        {String(getValue() ?? "—")}
                        <img
                            src={allNHorizontalImage}
                            alt="ALL-N Horizontal"
                            className="h-5"
                        />
                    </div>
                );
            }
            return (
                <span
                    className="font-medium text-default-900 cursor-pointer"
                    onClick={() => {
                        row.toggleSelected(!row.getIsSelected());
                    }}
                >
                    {String(getValue() ?? "—")}
                </span>
            );
        },
        size: 300,
    }),
    organization: <T extends ApplicationPlan>(
        organizations: { id: number; description: string }[],
    ): ColumnDef<T> => ({
        id: "organization",
        header: "Organization",
        accessorFn: (row) => row.organization,
        cell: ({ row }) => (
            <span className="text-default-600">
                {
                    organizations.find(
                        (o) => o.id === row.original.organization,
                    )?.description
                }
            </span>
        ),
        size: 200,
    }),
    applications: <T extends ApplicationPlan>(
        cropStageLookup: Record<number, CropStage>,
    ): ColumnDef<T> => ({
        id: "applications",
        header: "Application Targets",
        accessorFn: (row) => row.planned_applications?.length ?? 0,
        cell: ({ row }) => {
            const applicationStages = sortArrayByKey(
                row.original.planned_applications.filter(
                    (app) => !!cropStageLookup[app.target_crop_stage],
                ),
                (app) =>
                    cropStageLookup[app.target_crop_stage]?.days_after_planting,
            ).map(
                (app) =>
                    ` ${cropStageLookup[app.target_crop_stage]?.growth_stage}`,
            );

            return (
                <span className="text-default-600">
                    {applicationStages.join(", ")}
                </span>
            );
        },
        size: 200,
    }),
};
